import React, { useState, useRef } from "react";
import { API_BASE_URL, AICategories as DEFAULT_AI_CATEGORIES, AIBaseCategories as DEFAULT_AI_BASE_CATEGORIES } from "../../Common/Common";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { geocode, setKey, RequestType } from "react-geocode";
import { slice1Actions } from "../../store/reducer";
import Place from "../../Models/Place";
import { mapGoogleTypesToCategoryId } from "../../Models/GooglePlaces";
import { countries } from "../../Data/Countries";
import { cleanDestination } from "../../Common/DestinationValidation";
import { enrichPlaces, hasRealPhoto, isBadCoverUrl } from "../../Common/placeEnrichment";
import "./Modal.css";
import "./ImportConfirmationModal.css";

const PARSE_URL = `${API_BASE_URL}/parse_itinerary_image.php`;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const resolveCountry = (guess) => {
  const g = String(guess ?? "").trim().toLowerCase();
  return countries.find((c) => c.toLowerCase() === g) || null;
};
const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
const isoDate = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// One-step import of a day-by-day itinerary image (infographic, screenshot,
// photo or PDF): the AI returns destinations + places, the user reviews
// them, and both are added to the guide. Day numbers in the image become
// dates from the start date chosen here.
export default function ImportItineraryImageModal({ show, onHide, onBuildMyTrip, onBuildFromPlaces }) {
  const dispatch = useDispatch();
  const GuideSettings = useSelector((state) => state.GuideSettings);
  const AppSettings = useSelector((state) => state.AppSettings);
  const [wantPhotos, setWantPhotos] = useState(true);
  const [wantHours, setWantHours] = useState(true);
  const [wantInfo, setWantInfo] = useState(true);
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("itinerary"); // "itinerary" | "list"
  const [items, setItems] = useState([]);
  const [step, setStep] = useState("input"); // input | loading | review | working | error
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [status, setStatus] = useState("");
  const [dests, setDests] = useState([]);
  const [places, setPlaces] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return isoDate(d);
  });
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  const reset = () => {
    setStep("input");
    setFile(null);
    setPastedText("");
    setErrorMsg("");
    setStatus("");
    setDests([]);
    setPlaces([]);
    setItems([]);
    setMode("itinerary");
  };
  const handleClose = () => {
    if (abortRef.current) abortRef.current.abort();
    reset();
    onHide();
  };

  const chooseFile = (f) => {
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setErrorMsg("That file is over 10 MB. Try a smaller image.");
      return;
    }
    setErrorMsg("");
    setFile(f);
  };

  const handlePaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith("image/"));
    if (item) chooseFile(item.getAsFile());
  };

  const handleExtract = async () => {
    if (!file && !pastedText.trim()) return;
    setStep("loading");
    setErrorMsg("");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const body = file ? { file_base64: await fileToBase64(file), file_name: file.name, file_mime: file.type } : { text: pastedText };
      const r = await fetch(PARSE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const res = await r.json();
      if (!res || res.found !== true) {
        setErrorMsg((res && res.reason) || "Couldn't find an itinerary in that image.");
        setStep("error");
        return;
      }
      setMode(res.mode === "list" ? "list" : "itinerary");
      setItems((res.items || []).map((it) => ({ ...it, country: resolveCountry(it.country) || it.country, selected: true })));
      setDests(
        (res.destinations || []).map((d) => {
          const country = resolveCountry(d.country);
          return { ...d, country: country || d.country, valid: country !== null, selected: country !== null };
        })
      );
      setPlaces((res.places || []).map((p) => ({ ...p, selected: !p.optional })));
      setTitle(res.title || "");
      // Use the AI-extracted start date when the text/image actually stated
      // one ("Starting Oct 1, 2026") instead of always silently keeping the
      // today+30 default — matches mobile's identical fix. Found
      // 2026-09-28: an explicit stated date was being completely ignored
      // with no indication anything had been overridden.
      if (typeof res.start_date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(res.start_date)) {
        setStartDate(res.start_date);
      }
      setStep("review");
    } catch (e) {
      if (e.name === "AbortError") return;
      setErrorMsg("Something went wrong reaching the AI service. Please try again.");
      setStep("error");
    }
  };

  const startH = GuideSettings?.schedule_day_start ?? 9;
  const endH = GuideSettings?.schedule_day_end ?? 20;

  // Destinations to hand to the existing Destinations/Build My Trip flow.
  // A day-by-day plan keeps its day ranges; a plain list becomes one day
  // per item (editable on the Destinations screen).
  const buildDestinations = () => {
    const base = DateTime.fromISO(startDate, { zone: "utc" });
    const make = (city, country, firstDay, lastDay, k) =>
      cleanDestination({
        id: Date.now() + k,
        city,
        country,
        dateStart: base.plus({ days: firstDay - 1 }).set({ hour: startH }),
        dateEnd: base.plus({ days: Math.max(lastDay, firstDay) - 1 }).set({ hour: endH }),
      });
    if (mode === "list") {
      return items
        .filter((it) => it.selected && resolveCountry(it.country))
        .map((it, k) => make(it.city || it.name, resolveCountry(it.country), k + 1, k + 1, k));
    }
    const chosen = dests.filter((d) => d.selected && d.valid);
    const built = chosen.map((d, k) => make(d.city, d.country, d.first_day, d.last_day, k));
    // Two stops on the same day ("Day 1: Naples & Sorrento") would be
    // rejected as overlapping — split that day between them instead.
    const midH = Math.floor((startH + endH) / 2);
    for (let i = 1; i < chosen.length; i++) {
      if (chosen[i - 1].last_day === chosen[i].first_day) {
        built[i - 1].dateEnd = built[i - 1].dateEnd.set({ hour: midH });
        built[i].dateStart = built[i].dateStart.set({ hour: midH });
      }
    }
    return built;
  };

  const hasPlaces = mode === "itinerary" && places.some((p) => p.selected && dests[p.destination_index]?.valid);

  const handleImport = () => {
    // Destinations only (or a plain list): hand them to Build My Trip as
    // DATA (not by writing them onto window.guide_obj here) — the wizard
    // owns new-guide-vs-add, place search, photos, hours, schedule, cover
    // and itinerary, and is the only thing that should ever touch the
    // guide, inside its own onCreate (its actual commit point). Previously
    // wrote g.destinations immediately and hardcoded target "add" — closing
    // the wizard modal without completing it still left the guide's
    // ORIGINAL destinations overwritten, a real data-loss bug if the guide
    // was then saved; and "add" contradicted the very comment that used to
    // be here about letting the wizard decide. Matches mobile's identical
    // fix. Found 2026-09-28.
    if (!hasPlaces) {
      const built = buildDestinations();
      if (built.length === 0) {
        setErrorMsg("Nothing selected to import.");
        setStep("error");
        return;
      }
      handleClose();
      if (onBuildMyTrip) onBuildMyTrip(undefined, built);
      return;
    }
    // A plan that names specific places: create them (so they aren't
    // re-picked by search), then hand off to Build Trip from Places.
    runImport();
  };

  const runImport = async () => {
    setKey(process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY);
    setStep("working");
    const guide = window.guide_obj;
    const failed = [];
    const validDests = dests.map((d, i) => ({ ...d, i })).filter((d) => d.valid);

    // Same fallback pattern as BuildMyTripWizard.js — AppSettings' async
    // fetch may not have populated these yet.
    let aiCategories = DEFAULT_AI_CATEGORIES;
    let aiBaseCategories = DEFAULT_AI_BASE_CATEGORIES;
    try {
      const parsed = JSON.parse(AppSettings.AICategories);
      if (Array.isArray(parsed) && parsed.length > 0) aiCategories = parsed;
    } catch (e) { /* keep default */ }
    try {
      const parsed = JSON.parse(AppSettings.AIBaseCategories);
      if (Array.isArray(parsed) && parsed.length > 0) aiBaseCategories = parsed;
    } catch (e) { /* keep default */ }

    // Groups: one per destination, so Rome/Florence/… can be filtered.
    const groupIds = {};
    validDests.forEach((d) => {
      if (!places.some((p) => p.selected && p.destination_index === d.i)) return;
      const existing = guide.findGroupFromName(d.city);
      groupIds[d.i] = existing !== null && existing !== undefined ? (existing.id ?? existing) : guide.addGroup(d.city);
    });

    const created = [];
    const chosen = places.filter((p) => p.selected && dests[p.destination_index]?.valid);
    let n = 0;
    for (const p of chosen) {
      n++;
      setStatus(`Adding places… (${n} of ${chosen.length})`);
      const d = dests[p.destination_index];
      let place = guide.places.placesList.find((ex) => same(ex.Name, p.name) && same(ex.City, d.city));
      if (!place) {
        try {
          const response = await geocode(RequestType.ADDRESS, `${p.name}, ${d.city}, ${d.country}`);
          const { lat, lng } = response.results[0].geometry.location;
          place = guide.places.placesList.find(
            (ex) => same(ex.Name, p.name) && Math.abs(parseFloat(ex.Lat) - lat) < 0.0005 && Math.abs(parseFloat(ex.Longi) - lng) < 0.0005
          );
          if (!place) {
            const np = new Place(guide.places);
            np.Name = p.name;
            np.City = d.city;
            np.Country = d.country;
            np.Lat = lat;
            np.Longi = lng;
            np.Info = [p.optional ? "Optional" : "", p.must_book ? "Book ahead" : "", p.notes].filter(Boolean).join(" · ");
            // The AI's own free-text guess (e.g. "Museum", "Beach"), mapped
            // through the same fuzzy matcher Google's real place types go
            // through elsewhere (BuildMyTripWizard.js) — rides in the one
            // extraction call already made for this import, no extra paid
            // lookup per place.
            const catId = p.category ? mapGoogleTypesToCategoryId([p.category], aiCategories, aiBaseCategories) : null;
            if (catId) np.Category = [catId];
            const id = guide.places.addPlace(np);
            place = guide.places.getPlace ? guide.places.getPlace(id) || np : np;
          }
        } catch (err) {
          failed.push(p.name);
          continue;
        }
      }
      const gid = groupIds[p.destination_index];
      if (gid !== undefined && gid !== null) {
        place.Group = Array.isArray(place.Group) ? place.Group : [];
        if (!place.Group.includes(gid)) place.Group.push(gid);
      }
      created.push(place);
    }

    const built = buildDestinations();
    guide.destinations = [...(guide.destinations || []), ...built];
    const attrs = JSON.parse(JSON.stringify(guide.attributes));
    validDests.filter((d) => d.selected).forEach((d) => {
      if (!attrs.cities.includes(d.city)) attrs.cities.push(d.city);
      if (!attrs.countries.includes(d.country)) attrs.countries.push(d.country);
    });
    guide.attributes.modifyGuideAttributes(attrs);

    // Photos / Wikipedia info now, so Build Trip from Places (which does
    // not fetch them) starts from populated places; guide cover from the
    // first real photo when the guide has none.
    if ((wantPhotos || wantInfo) && created.length > 0) {
      await enrichPlaces(created, { photos: wantPhotos, info: wantInfo, hours: wantHours }, (done, total) =>
        setStatus(`Adding photos and info… (${done} of ${total})`)
      );
    }
    if (isBadCoverUrl(guide.attributes.image_m)) {
      // Skip the first real-photo place when a second exists — see the
      // identical fix in BuildMyTripWizard.js/HomeHeader.js. Requested
      // 2026-09-27.
      const photoPlaces = created.filter((p) => hasRealPhoto(p));
      const coverPlace = (photoPlaces.length > 1 ? photoPlaces[1] : photoPlaces[0]) || null;
      if (coverPlace) {
        const a = JSON.parse(JSON.stringify(guide.attributes));
        a.image = coverPlace.Photo || coverPlace.Photo_m;
        a.image_m = coverPlace.Photo_m || coverPlace.Photo;
        guide.attributes.modifyGuideAttributes(a);
      }
    }

    window.guest_generated_unsaved_trip = true;
    // Flags this as a guide with real content that now needs saving — this
    // path adds places directly (no BuildMyTripWizard hand-off, so no other
    // completion point covers it). Port of andiamo-places' identical fix.
    // Added 2026-09-30.
    dispatch(slice1Actions.setGuideHasUnsavedChanges({ value: true }));
    dispatch(slice1Actions.ClearAllFilters());
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true, update_guide_attributes: true }));
    setStatus("");
    if (failed.length > 0) {
      setErrorMsg(`Added ${created.length} places. Couldn't find: ${failed.join(", ")}. The Build Trip from Places menu will open next.`);
    }
    handleClose();
    if (onBuildFromPlaces) onBuildFromPlaces(created.map((p) => p.ID));
  };

  const toggleDest = (i) => setDests((ds) => ds.map((d, j) => (j === i ? { ...d, selected: !d.selected } : d)));
  const togglePlace = (i) => setPlaces((ps) => ps.map((p, j) => (j === i ? { ...p, selected: !p.selected } : p)));

  const renderInput = () => (
    <div onPaste={handlePaste}>
      <p style={{ fontSize: "0.9rem", color: "#666" }}>
        Paste text (an article, a list from a post, an itinerary) or upload an image or PDF of one: a day-by-day
        plan, a route, or a "Top 10" list. The AI reads the cities and places and hands them to Build My Trip.
      </p>
      <textarea
        className="form-control"
        rows={7}
        placeholder="Paste itinerary or list text here…"
        value={pastedText}
        onChange={(e) => {
          setPastedText(e.target.value);
          if (e.target.value) setFile(null);
        }}
        disabled={!!file}
        autoFocus
      />
      <div style={{ margin: "0.75rem 0", textAlign: "center", color: "#999", fontSize: "0.8rem" }}>— or —</div>
      <input
        type="file"
        accept="application/pdf,image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => chooseFile(e.target.files?.[0])}
      />
      <button className="btn btn-outline-secondary w-100" onClick={() => fileInputRef.current?.click()} disabled={!!pastedText.trim()}>
        {file ? `Selected: ${file.name}` : "Choose image or PDF…"}
      </button>
      {file && (
        <button className="btn btn-link btn-sm" onClick={() => setFile(null)} style={{ display: "block", margin: "0.25rem auto 0" }}>
          Remove file
        </button>
      )}
      {!pastedText && !file && (
        <p style={{ fontSize: "0.8rem", color: "#999", textAlign: "center", marginTop: "0.5rem" }}>
          You can also paste an image (Ctrl/Cmd+V) while this window is open.
        </p>
      )}
      {errorMsg && <p style={{ color: "#b00020" }}>{errorMsg}</p>}
    </div>
  );

  const renderReview = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>{mode === "itinerary" ? "Day 1 is" : "Trip starts"}</span>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </div>
      {hasPlaces && (
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ marginRight: "1rem" }}>
          <input type="checkbox" checked={wantPhotos} onChange={(e) => setWantPhotos(e.target.checked)} /> Photos
        </label>
        {(
          <label style={{ marginRight: "1rem" }}>
            <input type="checkbox" checked={wantHours} onChange={(e) => setWantHours(e.target.checked)} /> Opening times
          </label>
        )}
        <label>
          <input type="checkbox" checked={wantInfo} onChange={(e) => setWantInfo(e.target.checked)} /> Wikipedia info
        </label>
      </div>
      )}
      {mode === "list" && (
        <>
          <p style={{ fontSize: "0.85rem", color: "#666" }}>
            This looks like a list without days, so each item becomes a destination. Next you'll review them on the Destinations screen and Build My Trip finds places, photos and a schedule for each.
          </p>
          <h6>Destinations ({items.filter((it) => it.selected).length} selected)</h6>
          {items.map((it, i) => (
            <div key={i}>
              <label>
                <input
                  type="checkbox"
                  checked={it.selected}
                  onChange={() => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, selected: !x.selected } : x)))}
                />{" "}
                {it.rank ? `${it.rank}. ` : ""}
                <b>{it.name}</b>
                <span style={{ color: "#888", fontSize: "0.8rem" }}>{it.country ? ` · ${it.country}` : ""}</span>
                {!resolveCountry(it.country) && <span style={{ color: "#b00020" }}> (country not recognised)</span>}
              </label>
            </div>
          ))}
        </>
      )}
      {mode === "itinerary" && <h6>Destinations</h6>}
      {mode === "itinerary" && dests.map((d, i) => (
        <div key={i} style={{ marginBottom: "0.25rem" }}>
          <label>
            <input type="checkbox" checked={d.selected} disabled={!d.valid} onChange={() => toggleDest(i)} />{" "}
            <b>{d.city}</b>, {d.country || "?"} — Day {d.first_day}
            {d.last_day !== d.first_day ? `–${d.last_day}` : ""}
            {!d.valid && <span style={{ color: "#b00020" }}> (country not recognised)</span>}
          </label>
        </div>
      ))}
      {mode === "itinerary" && <h6 style={{ marginTop: "0.75rem" }}>Places ({places.filter((p) => p.selected).length} selected)</h6>}
      {mode === "itinerary" && dests.map((d, di) => {
        const list = places.map((p, i) => ({ p, i })).filter(({ p }) => p.destination_index === di);
        if (list.length === 0) return null;
        return (
          <div key={di} style={{ marginBottom: "0.5rem" }}>
            <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{d.city}</div>
            {list.map(({ p, i }) => (
              <div key={i} style={{ marginLeft: "0.75rem" }}>
                <label>
                  <input type="checkbox" checked={p.selected} disabled={!d.valid} onChange={() => togglePlace(i)} />{" "}
                  {p.name}
                  <span style={{ color: "#888", fontSize: "0.8rem" }}>
                    {p.day ? ` · Day ${p.day}` : ""}
                    {p.optional ? " · optional" : ""}
                    {p.must_book ? " · book ahead" : ""}
                  </span>
                </label>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="icm-dialog" size={step === "review" ? "lg" : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>Build Trip from Image or Text</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "1rem", overflowX: "auto" }}>
        {step === "input" && renderInput()}
        {(step === "loading" || step === "working") && (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <p>{step === "loading" ? "Reading your itinerary…" : status || "Working…"}</p>
          </div>
        )}
        {step === "error" && (
          <div>
            <p style={{ color: "#b00020" }}>{errorMsg}</p>
            <button className="btn btn-secondary btn-sm" onClick={() => setStep(dests.length ? "review" : "input")}>
              Back
            </button>
          </div>
        )}
        {step === "review" && renderReview()}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary btn-sm" onClick={handleClose}>
          Cancel
        </button>
        {step === "input" && (
          <button className="btn btn-primary btn-sm" onClick={handleExtract} disabled={!file && !pastedText.trim()}>
            Read Itinerary
          </button>
        )}
        {step === "review" && (
          <button className="btn btn-primary btn-sm" onClick={handleImport}>
            {hasPlaces ? "Add places & build schedule" : "Continue to Build My Trip"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
